/* eslint-disable react/forbid-prop-types */
import React from 'react';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import { Icon } from '@material-ui/core';
import get from 'lodash/get';
import { searchableSelectStyle } from '../../css/style';
import * as CompanyFeatures from '../../services/CompanyFeatures';

const styles = searchableSelectStyle();

const companyHasSharedOptions = (props) => {
  const { admin } = props.selectProps;
  const hasSharedOptions = CompanyFeatures.isSharedOptionsEnabled(admin);
  return hasSharedOptions;
};

/**
 * Used to prevent detaching the current modifier in an Edit Modifier page CreateEditOptionDialog
 * @param {*} props
 * @param {boolean} hasSharedOptions
 * @returns {boolean}
 */
const isCustomizationTagMatchingUrl = (props, hasSharedOptions) => {
  const { data } = props;
  const { match, fieldKey } = props.selectProps;
  if (!hasSharedOptions || fieldKey !== 'customizations') return false;
  const tagCustomizationId = data.value;
  const resourceId = getCurrentResourceId(match);
  return resourceId && tagCustomizationId === resourceId;
};

const getCurrentResourceId = (match) => {
  const resourceId = get(match, 'params.resourceId');
  return Number(resourceId);
};

const clearModifiersOtherThanCurrent = (arrOfLabelValueObjs, match) => {
  return arrOfLabelValueObjs.filter(({ value }) => value === getCurrentResourceId(match));
};

// The Clear all button, ClearIndicator is the react-select namespace override
// Some react-select internal components use a ref, pass it to the right component like the demo
// `innerProps` are all the props the component needs to function; spread them.
// Reference: https://react-select.com/components#replacing-components
// Demo for custom ClearIndicator and DropdownIndicator found in this section of the docs if you scroll down: https://react-select.com/components#replaceable-components
const ClearIndicator = (props) => {
  const {
    children,
    getStyles,
    innerProps: { ref, ...restInnerProps },
    clearValue,
    getValue,
    selectProps,
    setValue,
  } = props;

  const currentValue = getValue();
  const match = get(props, 'selectProps.match');
  const hasSharedOptions = companyHasSharedOptions(props);
  const isAttachedModifiersField = selectProps.fieldKey === 'customizations';

  const finalValue = hasSharedOptions && isAttachedModifiersField
    ? clearModifiersOtherThanCurrent(currentValue, match)
    : [];

  const finalClearValueFunc = hasSharedOptions && isAttachedModifiersField
    ? () => setValue(finalValue)
    : clearValue;

  return (
    <div
      // next three props are the same value as from this component's restInnerProps
      aria-hidden
      onMouseDown={finalClearValueFunc}
      onTouchEnd={finalClearValueFunc}
      ref={ref}
      style={getStyles('clearIndicator', props)}
    >
      <Icon style={{ fontSize: '20px', paddingRight: '5px' }}>close</Icon>
    </div>
  );
};

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <Icon style={{ fontSize: '25px' }}>arrow_drop_down</Icon>
  </components.DropdownIndicator>
);

const MultiValueRemove = (props) => {
  const {
    children,
    innerProps: { ref, ...restInnerProps },
  } = props;

  const functionalityProps = {
    ...restInnerProps,
    ref,
  };

  const hasSharedOptions = companyHasSharedOptions(props);
  const isMatchingCustomizationTag = isCustomizationTagMatchingUrl(props, hasSharedOptions);

  if (isMatchingCustomizationTag) {
    return (
      <div style={styles.tagRightSegmentWithoutRemove} />
    );
  }

  return (
    <div {...functionalityProps} style={styles.removeTagButtonParent}>
      <Icon style={styles.multiValueRemove}>close</Icon>
    </div>
  );
};

// Styling API of react-select, writing the styling functions below https://react-select.com/styles#provided-styles-and-state
// Keys for the different internal components: https://react-select.com/styles#style-object
// Demo of the different components: https://react-select.com/components#replaceable-components
const customStyles = props => (
  {
    noOptionsMessage: () => styles.noOptionsMessage,
    container: provided => ({
      ...provided,
      ...styles.container,
    }),
    control: (provided, state) => ({
      ...provided,
      ...styles.control,
      ...props.controlStyleOverride,
    }),
    placeholder: provided => ({
      ...provided,
      ...styles.placeholder,
    }),
    menu: provided => ({
      ...provided,
      ...styles.menu,
    }),
    // https://react-select.com/styles#select-props - Scroll to heading "Customised styles for Multi Select"
    option: (provided, {
      data, isDisabled, isFocused, isSelected,
    }) => {
      if (isDisabled) return { ...provided, ...styles.disabledOption };
      if (isFocused) return { ...provided, ...styles.focusedOption };
      if (isSelected) return { ...provided, ...styles.selectedOption };
      return { ...provided, ...styles.unFocusedOption };
    },
    multiValueContainer: (provided, state) => ({
      ...provided,
      ...props.multiValueContainerStyleOverride,
    }),
    multiValueLabel: provided => ({
      ...provided,
      ...styles.multiValueLabel,
      ...props.multiValueLabelStyleOverride,
    }),
    singleValue: provided => ({
      ...provided,
      ...styles.singleValue,
    }),
    valueContainer: provided => ({
      ...provided,
      ...styles.valueContainer,
    }),
    clearIndicator: provided => ({
      ...provided,
      ...props.clearIndicatorStyleOverride,
    }),
    dropdownIndicator: provided => ({
      ...provided,
      ...props.dropdownIndicatorStyleOverride,
    }),
    input: provided => ({
      ...provided,
      ...styles.input,
    }),
  }
);

const customComponents = {
  ClearIndicator,
  DropdownIndicator,
  MultiValueRemove,
};
function SearchableSelect(props) {
  const { type } = props;
  const SelectComponent = type || Select;
  return (
    <SelectComponent
      {...props}
      components={customComponents}
      styles={customStyles(props)}

    />
  );
}

SearchableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.func,
  controlStyleOverride: PropTypes.objectOf(PropTypes.string),
  multiValueLabelStyleOverride: PropTypes.objectOf(PropTypes.string),
  multiValueRemoveStyleOverride: PropTypes.objectOf(PropTypes.any),
  clearIndicatorStyleOverride: PropTypes.objectOf(PropTypes.string),
  dropdownIndicatorStyleOverride: PropTypes.objectOf(PropTypes.string),
};

SearchableSelect.defaultProps = {
  options: null,
  value: null,
  type: null,
  controlStyleOverride: null,
  multiValueLabelStyleOverride: null,
  multiValueRemoveStyleOverride: null,
  clearIndicatorStyleOverride: null,
  dropdownIndicatorStyleOverride: null,
};

export default SearchableSelect;
